import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUserProfileViewModel } from '../viewmodels/UserProfileViewModel';
import { useAuth } from '../contexts/AuthContext';
import { ActionRow, Avatar, BigButton, FIELD, FieldLabel, SectionLabel, StatusPill } from '../components/ui/dg';

/**
 * Mon profil : la carte de la personne connectée à gauche, ses informations
 * modifiables et l'état de son compte à droite.
 */

const ROLE_LABEL: Record<string, string> = { admin: 'Administrateur', moderator: 'Modérateur', user: 'Utilisateur' };
const card = 'rounded-[20px] border border-[#EEF0F4] bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]';
const photoUrl = (url?: string | null) => (!url ? null : /^https?:\/\//.test(url) ? url : `${process.env.REACT_APP_API_URL || ''}${url.replace(/^\//, '')}`);
const date = (value?: string, withTime = false) => {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('fr-FR', withTime ? { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' } : { day: 'numeric', month: 'short', year: 'numeric' });
};

const UserProfile = () => {
  const { state, updateField, loadProfile, updateProfile, isFormValid } = useUserProfileViewModel();
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null);
  const u = user as any;

  useEffect(() => {
    if (u?.id_user && token) loadProfile();
  }, [u?.id_user, token, loadProfile]);

  const name = [state.firstName || u?.first_name, state.lastName || u?.last_name].filter(Boolean).join(' ') || u?.email || 'Mon compte';

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setNotice(null);
    updateProfile().then((result) => {
      if (result?.success) setNotice({ ok: true, text: 'Profil mis à jour.' });
      else if (result?.message) setNotice({ ok: false, text: result.message });
    });
  };

  const account: [string, React.ReactNode][] = [
    ['Statut', <StatusPill tone={u?.is_suspended ? 'red' : 'green'}>{u?.is_suspended ? 'Bloqué' : 'Actif'}</StatusPill>],
    ['Rôle', ROLE_LABEL[u?.role] || u?.role || '—'],
    ['Membre depuis', date(u?.registration_date || u?.created_at)],
    ['Dernière connexion', date(u?.last_login, true)]
  ];

  return (
    <main className="flex-1 overflow-y-auto px-4 pb-6 pt-1 lg:px-7">
      <div className="flex flex-col items-start gap-5 xl:flex-row">
        <section className={`${card} w-full px-6 pb-4 pt-7 text-center xl:w-[360px] xl:shrink-0`}>
          <div className="inline-flex"><Avatar name={name} photo={photoUrl(u?.profile_photo)} size={88} ring /></div>
          <h2 className="mt-3.5 text-[22px] font-extrabold text-[#1E293B]">{name}</h2>
          <p className="text-[13.5px] text-[#6B7280]">{state.email || u?.email}</p>
          <div className="mt-2.5 flex justify-center gap-1.5">
            <StatusPill tone="indigo">{ROLE_LABEL[u?.role] || u?.role || 'Compte'}</StatusPill>
            {u?.is_checked ? <StatusPill tone="green">Compte vérifié</StatusPill> : <StatusPill tone="amber">Non vérifié</StatusPill>}
          </div>
          <div className="-mx-6 mt-5 text-left">
            <ActionRow first icon="lock" title="Mot de passe" subtitle="Dans les paramètres" tone="grey" onClick={() => navigate('/settings')} inset={24} />
            <ActionRow icon="M14 4h5v16h-5M10 8l-4 4 4 4M6 12h10" title="Se déconnecter" tone="red" onClick={() => logout()} inset={24} />
          </div>
        </section>

        <div className="w-full min-w-0 flex-1 space-y-5">
          <form onSubmit={submit} className={`${card} px-7 pb-6 pt-[22px]`}>
            <SectionLabel>Mes informations</SectionLabel>
            <div className="grid grid-cols-1 gap-x-4 md:grid-cols-2">
              <div>
                <FieldLabel htmlFor="p-first">Prénom</FieldLabel>
                <input id="p-first" value={state.firstName} onChange={(e) => updateField('firstName', e.target.value)} placeholder="Votre prénom" className={FIELD} />
              </div>
              <div>
                <FieldLabel htmlFor="p-last">Nom</FieldLabel>
                <input id="p-last" value={state.lastName} onChange={(e) => updateField('lastName', e.target.value)} placeholder="Votre nom" className={FIELD} />
              </div>
              <div>
                <FieldLabel htmlFor="p-email">E-mail</FieldLabel>
                <input id="p-email" type="email" value={state.email} onChange={(e) => updateField('email', e.target.value)} placeholder="vous@exemple.com" className={FIELD} />
              </div>
              <div>
                <FieldLabel htmlFor="p-phone">Téléphone</FieldLabel>
                <input id="p-phone" type="tel" value={state.phone} onChange={(e) => updateField('phone', e.target.value)} placeholder="+216 22 345 678" className={FIELD} />
              </div>
              <div>
                <FieldLabel htmlFor="p-birth">Date de naissance</FieldLabel>
                <input id="p-birth" type="date" value={state.birthDate} onChange={(e) => updateField('birthDate', e.target.value)} className={FIELD} />
              </div>
            </div>
            {notice && <p className={`mt-5 rounded-xl px-3 py-2 text-[13px] ${notice.ok ? 'bg-[#ECFDF5] text-[#047857]' : 'bg-[#FDECEF] text-[#B91C1C]'}`}>{notice.text}</p>}
            <div className="mt-6 flex justify-end gap-3">
              <BigButton kind="secondary" onClick={() => { loadProfile(); setNotice(null); }}>Annuler</BigButton>
              <BigButton type="submit" disabled={state.isLoading || !isFormValid()} className="min-w-[200px]">
                {state.isLoading ? 'Enregistrement…' : 'Enregistrer'}
              </BigButton>
            </div>
          </form>

          <section className={`${card} px-7 pb-5 pt-[22px]`}>
            <SectionLabel>Mon compte</SectionLabel>
            <dl className="mt-3 grid grid-cols-2 gap-4 md:grid-cols-4">
              {account.map(([label, value]) => (
                <div key={label}>
                  <dt className="text-[12.5px] text-[#6B7280]">{label}</dt>
                  <dd className="mt-1 text-[14px] font-bold text-[#1E293B]">{value}</dd>
                </div>
              ))}
            </dl>
          </section>
        </div>
      </div>
    </main>
  );
};

export default UserProfile;
